import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Collapse } from 'react-bootstrap';
import { Plus, Edit, Trash2, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchSections, createSection, updateSection, deleteSection } from './redux/sectionSlice';
import { fetchClasses } from '../classes/redux/classSlice';
import { fetchTeachers } from '../teachers/redux/teacherSlice';
import { distance } from 'mathjs';
import { getMasterDataByUserId } from '../../../pages/masterData/redux/masterDataThunk';
import { useLocation, useNavigate } from 'react-router-dom';
import Joyride from 'react-joyride';
import { sectionSteps } from '../../../tour/timetableTourSteps';
import { setStepValid, startTour, stopTour } from '../../../redux/globalJoyrideSlice';
import GlobalJoyrideCloseButton from '../../../components/GlobalJoyrideCloseButton';

const SectionManagement = () => {
  const dispatch = useDispatch();
  const { sections, loading, error } = useSelector(state => state.sections);
  const { classes } = useSelector(state => state.classes);
  const { teachers } = useSelector(state => state.teachers);

  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [subjectAssignments, setSubjectAssignments] = useState({});
  const [tourSectionId, setTourSectionId] = useState(null);

  const { user } = useSelector(state => state.authSlice);
  const schoolId = useSelector((state) => state.masterData.school_id)
  const navigate = useNavigate();


  useEffect(() => {
    if (user?.id) {
      dispatch(getMasterDataByUserId(user.id));
    }
  }, [dispatch, user?.id]);

  const location = useLocation();
  const globalJoyride = useSelector(state => state.globalJoyride);
   const isSectionTourRunning =
    globalJoyride.run && globalJoyride.activeTour === "section";

  useEffect(() => {
    if (!location.state?.startSectionTour) return;
    if (!sections.length) return;

    dispatch(startTour({
      name: "section",
      steps: sectionSteps(),
    }));

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, sections.length]);

  useEffect(() => {
    if (schoolId) {
      dispatch(fetchSections(schoolId));
      dispatch(fetchClasses());
      dispatch(fetchTeachers());
    }
  }, [dispatch, schoolId]);

  const toggleExpansion = (id, section) => {
    const updated = new Set(expandedSections);
    if (updated.has(id)) {
      updated.delete(id);
      setTourSectionId(null);
    } else {
      updated.add(id);
      setTourSectionId(id);  // Track which section is being toured
      // Initialize subject assignments with current teachers
      const assignments = {};
      section.subjects?.forEach(subject => {
        if (subject.current_teacher) {
          assignments[subject.subject_id] = subject.current_teacher.teacher_id;
        }
      });
      setSubjectAssignments(prev => ({ ...prev, [id]: assignments }));
    }
    setExpandedSections(updated);
  };

  const handleInlineChange = (sectionId, subjectId, teacherId) => {
    setSubjectAssignments(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [subjectId]: teacherId
      }
    }));
  };

  const handleUpdateTeachers = async (section) => {
    if (!user?.id) {
      alert('User ID is missing. Cannot update section.');
      return;
    }

    const selected = subjectAssignments[section.section_id] || {};
    const updatedTeachers = Object.entries(selected)
      .filter(([_, teacherId]) => teacherId) // Filter out empty selections
      .map(([subject_id, teacher_id]) => ({
        subject_id: parseInt(subject_id),
        teacher_id
      }));

    try {
      await dispatch(updateSection({
        id: section.section_id,
        data: {
          section_name: section.section_name,
          class_id: section.class_id,
          class_teacher_id: section.class_teacher?.teacher_id || null,
          subject_teachers: updatedTeachers,
          user_id: user.id
        }
      })).unwrap();

      alert('Teachers updated successfully');
      if (schoolId) {
        dispatch(fetchSections(schoolId));
      }
      // ✅ Only control navigation if this action came from the tour
      if (isSectionTourRunning) {
        dispatch(stopTour());

        setTimeout(() => {
          navigate("/timetable/periods", {
            state: { startPeriodTour: true }
          });
        }, 300);
      }

    } catch (err) {
      console.error('Update failed:', err);
      alert('Update failed: ' + (err.message || 'Please try again'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolId || !user?.id) {
      alert('School ID or User ID is missing. Cannot create section.');
      return;
    }

    try {
      await dispatch(createSection({
        ...formData,
        school_id: schoolId,
        user_id: user.id
      })).unwrap();
      setShowModal(false);
      setFormData({ section_name: '', class_id: '', class_teacher_id: '', subject_teachers: [] });
      if (schoolId) {
        dispatch(fetchSections(schoolId));
      }
    } catch (error) {
      console.error('Error creating section:', error);
      alert(`Error creating section: ${error.message || 'Please try again'}`);
    }
  };

  const handleDelete = async (id) => {
    if (!user?.id) {
      alert('User ID is missing. Cannot delete section.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await dispatch(deleteSection({ id, user_id: user.id })).unwrap();
        if (schoolId) {
          dispatch(fetchSections(schoolId));
        }
      } catch (error) {
        console.error('Error deleting section:', error);
        alert(`Error deleting section: ${error.message || 'Please try again'}`);
      }
    }
  };

  const [formData, setFormData] = useState({
    section_name: '',
    class_id: '',
    class_teacher_id: '',
    subject_teachers: []
  });

  return (
    <>
      {/* <GlobalJoyrideCloseButton /> */}
      <div className='px-5' >
        <div className="px-2">
          <Row className="py-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                {/* <div>
              <h2 className="text-primary fw-bold mb-0">Section Management</h2>
              <p className="text-muted">Manage sections and assign teachers</p>
            </div> */}
                {/* <Button
              variant="btn btn-primary btn-sm"
              onClick={() => setShowModal(true)}
              className="d-flex align-items-center"
            >
              <Plus size={20} className="me-2" />
              Add Section
            </Button> */}
              </div>
            </Col>
          </Row>

          {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

          <Card>
            <Card.Header style={{
              // backgroundColor: "#1e88e5",
              // background: "linear-gradient(to right, #7B2FF7, #F40076)",
              background: "linear-gradient(to right, #7B2FF7 50%, #F40076 100%)",
              fontSize: "0.85rem",
              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
              color: "white"
            }}>
              <h5 className="mb-0 d-flex align-items-center">
                <Calendar size={24} className="me-2" />
                All Sections
              </h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Section Name</th>
                      <th>Class</th>
                      <th>Class Teacher</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map((section) => (
                      <React.Fragment key={section.section_id}>
                        <tr>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => {
                                toggleExpansion(section.section_id, section);
                                dispatch(setStepValid(true));   // 🔓 unlock Next
                              }}
                              className="section-expand-step"
                            >
                              {expandedSections.has(section.section_id) ? <ChevronDown /> : <ChevronRight />}
                            </Button>
                          </td>
                          <td>{section.section_name}</td>
                          <td>{section.class_name}</td>
                          <td>{section.class_teacher?.teacher_name || 'Not assigned'}</td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(section.section_id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="5" className="p-0 border-0">
                            <Collapse in={expandedSections.has(section.section_id)}>
                              <div className="p-3 bg-light">
                                <h6 className="text-muted mb-3">Subject Teachers</h6>
                                {section.subjects?.length > 0 ? (
                                  section.subjects.map((subject, index) => (
                                    <Row key={subject.subject_id} className="align-items-center mb-2">
                                      <Col md={4}>
                                        <span className="fw-semibold">{subject.subject_name}</span>
                                        {/* {subject.current_teacher && (
                                      <span className="text-muted ms-2">
                                        (Current: {subject.current_teacher.teacher_name})
                                      </span>
                                    )} */}
                                      </Col>
                                      <Col md={8}>
                                        <Form.Select
                                          size="sm"
                                          value={subjectAssignments[section.section_id]?.[subject.subject_id] || ''}
                                          onChange={(e) => {
                                            handleInlineChange(
                                              section.section_id,
                                              subject.subject_id,
                                              e.target.value
                                            );
                                            dispatch(setStepValid(true));   // 🔓 unlock Next
                                          }}
                                          className={index === 0 && tourSectionId === section.section_id ? "subject-teacher-select-step" : ""}
                                        >
                                          <option value="">Select Teacher</option>
                                          {subject.available_teachers.map(teacher => (
                                            <option
                                              key={teacher.teacher_id}
                                              value={teacher.teacher_id}
                                            >
                                              {teacher.teacher_name}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      </Col>
                                    </Row>
                                  ))
                                ) : (
                                  <p>No subjects assigned to this section.</p>
                                )}
                                <div className="text-end mt-3">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleUpdateTeachers(section)}
                                    className={tourSectionId === section.section_id ? "update-teachers-step" : ""}
                                  >
                                    Update Teachers
                                  </Button>
                                </div>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
            <Form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Add New Section</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Section Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.section_name}
                        onChange={(e) => setFormData({ ...formData, section_name: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Class</Form.Label>
                      <Form.Select
                        value={formData.class_id}
                        onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                        required
                      >
                        <option value="">Select Class</option>
                        {classes?.map(cls => (
                          <option key={cls.class_id} value={cls.class_id}>
                            {cls.class_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Class Teacher</Form.Label>
                  <Form.Select
                    value={formData.class_teacher_id}
                    onChange={(e) => setFormData({ ...formData, class_teacher_id: e.target.value })}
                    required
                  >
                    <option value="">Select Teacher</option>
                    {teachers.length > 0 && teachers?.map(t => (
                      <option key={t.teacher_id} value={t.teacher_id}>
                        {t.name} ({t.qualification})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Create Section
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default SectionManagement;